import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import EprDao from "@dao/EprDao";

import { DASH_LINKS } from "@const/AppCardLinks";
import { formatDate, getEpoch, getTodayWithTime } from "@utils/DateFormatter";
import { GlobalContext } from "@components/contextProvider/ContextProvider";
import { grades } from "@const/Grades";

import type { AppObject } from "@/types/GlobalState";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ActionWindow {
    startDate: string;
    endDate: string;
    action: string;
    differentiator: string;
    whoType: string;
}

interface WindowStatus {
    isActive: boolean;
    nextStartDate: string | null;
    start: string | null;
    end: string | null;
}

interface StudentDto {
    key?: string;
    studentNumber?: string | number;
    locationKey?: string | number;
    schoolId?: string | number;
    gradeName?: string;
    firstName?: string;
}

interface CharterSchoolEntry {
    studentNumber: string | number;
    isCharter: boolean;
}

interface HomeSchooledEntry {
    studentNumber: string | number;
    isHomeSchooled: boolean;
}

interface LocationDto {
    key: string | number;
    icId?: (string | number)[];
}

interface SchoolYearDto {
    key: string | number;
}

interface EprStatusPayload {
    [key: string]: unknown;
}

// ─── Helper ───────────────────────────────────────────────────────────────────

/**
 * Helper to determine window status based on logic provided
 * @param {ActionWindow[]} windows - List of action windows
 * @returns {WindowStatus} { isActive, nextStartDate, start, end }
 */
const getWindowStatus = (windows: ActionWindow[]): WindowStatus => {
    if (!windows || windows.length === 0) {
        return { isActive: false, nextStartDate: null, start: null, end: null };
    }

    const now = getEpoch(getTodayWithTime());
    if (now === null) return { isActive: false, nextStartDate: null, start: null, end: null };
    let isActive = false;
    let nextStart: string | null = null;
    let activeStart: string | null = null;
    let activeEnd: string | null = null;

    windows.forEach((action: ActionWindow) => {
        const start = getEpoch(action.startDate);
        const end = getEpoch(action.endDate);

        if (start === null || end === null) return;

        // Check if currently active (Inclusive)
        if (start <= now && now <= end) {
            isActive = true;
            activeStart = formatDate(action.startDate, true, true);
            activeEnd = formatDate(action.endDate, true, true);
        }

        // Check for future windows
        if (start > now) {
            const nextStartEpoch = nextStart ? getEpoch(nextStart) : null;
            if (!nextStart || (nextStartEpoch !== null && start < nextStartEpoch)) {
                nextStart = formatDate(action.startDate, true, true);
            }
        }
    });

    return {
        isActive,
        nextStartDate: isActive ? null : nextStart,
        start: activeStart,
        end: activeEnd
    };
};

// ─── App Object Helpers ──────────────────────────────────────────────────────

const resolveWindows = (eprActionWindows: ActionWindow[] | null): ActionWindow[] => {
    const viewActions = eprActionWindows?.filter((a) => a.action === "EPR_PARENT_VIEW") || [];
    const byDifferentiator: Record<string, ActionWindow> = {};

    viewActions.forEach((action) => {
        const current = byDifferentiator[action.differentiator];
        if (current?.whoType === "LOCATION") return;
        if (action.whoType === "LOCATION" || (action.whoType === "DEFAULT" && !current)) {
            byDifferentiator[action.differentiator] = action;
        }
    });

    return Object.values(byDifferentiator);
};

const applyActiveStatus = (
    baseApp: AppObject,
    eprs: EprStatusPayload | false | null,
    studentGrade: string,
    elementaryGrades: string[],
    middleGrades: string[],
    currentStudentNumber: string | number
): void => {
    if (eprs) {
        baseApp.status = "EPRApp_IsAvailable";
        baseApp.statusColor = "#4a8319";
        if (elementaryGrades.includes(studentGrade)) {
            baseApp.buttons?.unshift({
                label: "EPRApp_Btn1",
                href: `${DASH_LINKS.epr}/recent/${currentStudentNumber}`
            });
            baseApp.broadcast = EPR_SURVEY_BROADCAST;
        }
    } else if (!middleGrades.includes(studentGrade)) {
        baseApp.status = "EPRApp_NotReleased";
        baseApp.statusColor = "#707070";
        baseApp.notice = ["EPRApp_NotReady"];
    }
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseEprAppProps {
    student: StudentDto;
    enabled?: boolean;
}

/**
 * EPR parent survey broadcast — hardcoded date window.
 * Replace with a BE-driven approach when available.
 */
const EPR_SURVEY_BROADCAST = ((): { description: string; linkText: string; url: string } | null => {
    const rightNow = Date.now();
    const start = getEpoch("2026-06-02 00:00:00");
    const end = getEpoch("2026-07-30 23:59:59");
    if (rightNow >= (start || 0) && rightNow <= (end || 0)) {
        return {
            description: "EPRApp_SurveyDescription",
            linkText: "EPRApp_SurveyLink",
            url: "https://docs.google.com/forms/d/e/1FAIpQLSfmnDxeN64EvUG6ainYfc7ifSbphwWz5dVPY2kr3CLzpbsTeQ/viewform"
        };
    }
    return null;
})();

/**
 * Custom hook to determine if a student's guardian has access to an Elementary Progress Report
 * @name useEprApp
 * @param {UseEprAppProps} props
 * @returns {{appObj: AppObject | null, loading: boolean}}
 */
export const useEprApp = ({
    student,
    enabled = true
}: UseEprAppProps): { appObj: AppObject | null; loading: boolean } => {
    const context = useContext(GlobalContext);
    const { dispatch, state } = context ?? {};
    const { allLocationDtos, schoolYearDto, studentAppObjects, studentsCharterSchool, studentsHomeSchooled } =
        (state as {
            allLocationDtos?: LocationDto[];
            schoolYearDto?: SchoolYearDto;
            studentAppObjects?: Record<string, Record<string, { appObj: AppObject | null }>>;
            studentsCharterSchool?: CharterSchoolEntry[];
            studentsHomeSchooled?: HomeSchooledEntry[];
        }) || {};

    const studentKey = student?.key;
    const currentStudentNumber = student?.studentNumber;

    // Access existing object wrapper from context
    const existingWrapper = studentKey ? studentAppObjects?.[studentKey]?.EPR : undefined;
    const existingAppObj = existingWrapper?.appObj;

    // 1. Local State
    const [apiData, setApiData] = useState<{
        eprActionWindows: ActionWindow[] | null;
        eprs: EprStatusPayload | false | null;
        loading: boolean;
        fetched: boolean;
        studentNumber: string | number | null | undefined;
    }>({
        eprActionWindows: null,
        eprs: null,
        loading: true,
        fetched: false,
        studentNumber: null
    });

    // 2. Derived Synchronous Data
    const locationDto = useMemo((): LocationDto | null => {
        if (!allLocationDtos || !student) return null;
        const { locationKey, schoolId } = student;

        if (schoolId) {
            const match = allLocationDtos.find((obj: LocationDto) => {
                if (!obj.icId || !Array.isArray(obj.icId)) return false;
                return obj.icId.some(
                    (id: string | number) => Number.parseInt(String(id), 10) === Number.parseInt(String(schoolId), 10)
                );
            });
            return match || null;
        }
        if (locationKey) {
            return (
                allLocationDtos.find(
                    (obj: LocationDto) =>
                        Number.parseInt(String(obj.key), 10) === Number.parseInt(String(locationKey), 10)
                ) || null
            );
        }
        return null;
    }, [allLocationDtos, student]);

    const isCharter = useMemo((): boolean => {
        if (!currentStudentNumber) return false;
        return (
            studentsCharterSchool?.some(
                (csObj: CharterSchoolEntry) =>
                    Number.parseInt(String(csObj.studentNumber), 10) ===
                        Number.parseInt(String(currentStudentNumber), 10) && csObj.isCharter
            ) ?? false
        );
    }, [studentsCharterSchool, currentStudentNumber]);

    const isHomeSchooled = useMemo((): boolean => {
        if (!currentStudentNumber) return false;
        return (
            studentsHomeSchooled?.some(
                (hsObj: HomeSchooledEntry) =>
                    Number.parseInt(String(hsObj.studentNumber), 10) ===
                        Number.parseInt(String(currentStudentNumber), 10) && hsObj.isHomeSchooled
            ) ?? false
        );
    }, [studentsHomeSchooled, currentStudentNumber]);

    const isHighSchool = useMemo((): boolean => {
        if (!currentStudentNumber) return true;
        const gradeName = student?.gradeName;
        if (!gradeName) return true;

        const studentGrade = grades(gradeName);
        const elementaryGrades = ["K", "01", "02", "03", "04", "05", "06"];
        const middleGrades = ["07", "08"];

        return ![...elementaryGrades, ...middleGrades].includes(studentGrade);
    }, [currentStudentNumber, student]);

    const isMiddleSchool = useMemo((): boolean => {
        if (!currentStudentNumber) return true;
        const gradeName = student?.gradeName;
        if (!gradeName) return true;

        const studentGrade = grades(gradeName);
        const middleGrades = ["07", "08"];

        return [...middleGrades].includes(studentGrade);
    }, [currentStudentNumber, student]);

    useEffect(() => {
        const isMissingInfo = !locationDto || !schoolYearDto || !currentStudentNumber;
        const isIneligible = isCharter || isHomeSchooled || isHighSchool;
        const shouldSkip = !enabled || existingWrapper || isMissingInfo || isIneligible;

        if (shouldSkip) {
            setApiData((prev) => {
                if (prev.loading) {
                    return { ...prev, loading: false };
                }
                return prev;
            });
            return;
        }

        if (apiData.fetched && apiData.studentNumber === currentStudentNumber) {
            return;
        }

        const controller = new AbortController();
        const { signal } = controller;

        const fetchData = async (): Promise<void> => {
            setApiData((prev) => ({ ...prev, loading: true }));

            try {
                const windowsPromise = EprDao<{ payload: ActionWindow[] }>({
                    action: "eprActionWindowsRead",
                    locationKey: locationDto.key,
                    schoolYearKey: schoolYearDto.key,
                    signal
                });
                /* eslint-disable-indent */
                const eprsPromise = isMiddleSchool
                    ? Promise.resolve(null)
                    : EprDao<{ payload: EprStatusPayload }>({
                          action: "eprStatusRead",
                          schoolYearKey: schoolYearDto.key,
                          studentNumber: currentStudentNumber,
                          signal
                      }).catch((err: unknown) => {
                          if ((err as { name?: string })?.name !== "AbortError") {
                              console.error("EPR Status Read failed");
                          }
                          return null;
                      });
                /* eslint-enable */
                const [windowsRes, eprsRes] = await Promise.all([windowsPromise, eprsPromise]);
                if (!signal.aborted) {
                    setApiData({
                        eprActionWindows: windowsRes?.data?.payload || [],
                        eprs: eprsRes?.data?.payload || false,
                        loading: false,
                        fetched: true,
                        studentNumber: currentStudentNumber
                    });
                }
            } catch (error: unknown) {
                if ((error as { name?: string })?.name === "AbortError") {
                    // This is an expected cancellation, do not log as an error.
                } else {
                    console.error("Error fetching EPR data:", error);
                }
                if (!signal.aborted) {
                    setApiData((prev) => ({ ...prev, loading: false }));
                }
            }
        };

        void fetchData();

        return (): void => controller.abort();
    }, [
        apiData.fetched,
        apiData.studentNumber,
        currentStudentNumber,
        enabled,
        existingWrapper,
        isCharter,
        isHighSchool,
        isHomeSchooled,
        isMiddleSchool,
        locationDto,
        schoolYearDto
    ]);

    // 4. Construct the App Object
    const appObj = useMemo((): AppObject | null => {
        if (existingWrapper) return existingAppObj ?? null;

        if (!currentStudentNumber) return null;
        if (!enabled || !student || isCharter || isHomeSchooled) return null;
        if (apiData.fetched && apiData.studentNumber !== currentStudentNumber) return null;

        const gradeName = student?.gradeName;
        if (!gradeName) return null;

        const studentGrade = grades(gradeName);
        const elementaryGrades = ["K", "01", "02", "03", "04", "05", "06"];
        const middleGrades = ["07", "08"];
        const isEligibleGrade = [...elementaryGrades, ...middleGrades].includes(studentGrade);

        if (!isEligibleGrade) return null;

        const baseApp: AppObject = {
            app: "EPR",
            title: "EPRApp_Header",
            body: "EPRApp_Body",
            broadcast: null,
            icon: "EPR_ICON",
            status: "EPRApp_NotAvailable",
            statusColor: "#0669B3", // Pearl Blue
            startDate: null,
            endDate: null,
            subBody: null,
            subStatus: null,
            subStatusRange: null,
            statusNumber: null,
            moreInfo: {},
            notice: null,
            buttons: [
                {
                    label: "EPRApp_Btn2", // View Archived EPRs
                    link: `${DASH_LINKS.epr}/archive/${currentStudentNumber}`
                }
            ]
        };

        if (!apiData.fetched) return null;

        const { eprActionWindows, eprs } = apiData;

        const validWindows = resolveWindows(eprActionWindows);
        const { isActive, nextStartDate, start, end } = getWindowStatus(validWindows);

        if (start && end) {
            baseApp.startDate = start;
            baseApp.endDate = end;
        }

        if (nextStartDate && !middleGrades.includes(studentGrade)) {
            baseApp.notice = ["EPRApp_Available", nextStartDate, ""];
        }

        if (isActive) {
            applyActiveStatus(baseApp, eprs, studentGrade, elementaryGrades, middleGrades, currentStudentNumber);
        }

        return baseApp;
    }, [existingWrapper, existingAppObj, enabled, student, currentStudentNumber, isCharter, isHomeSchooled, apiData]);

    // 5. Dispatch Logic
    const dispatchAppObject = useCallback(
        (obj: AppObject | null) => {
            const payload = {
                ...studentAppObjects,
                [studentKey!]: {
                    ...studentAppObjects?.[studentKey!],
                    EPR: {
                        appObj: obj
                    }
                }
            };

            dispatch?.({
                type: "StudentAppObjects",
                studentAppObjects: payload
            });
        },
        [dispatch, studentAppObjects, studentKey]
    );

    useEffect(() => {
        if (!existingWrapper && appObj && apiData.fetched && apiData.studentNumber === currentStudentNumber) {
            dispatchAppObject(appObj);
        }
    }, [appObj, existingWrapper, apiData, currentStudentNumber, dispatchAppObject]);

    return { appObj: (existingWrapper ? existingAppObj : appObj) ?? null, loading: apiData.loading };
};
